import type { StatusTone } from "@/design-system/components/ui";
import type {
  ListingAgentResearchConfidence,
  ListingAgentResearchOrigin,
  ListingAgentResearchRunStatus,
  ListingAgentResearchSourceKind,
} from "@/api/listingAgentResearch";

/** The workspace's five stages (D8), in rail order. Any stage, any time. */
export const STAGES = [
  { key: "start", label: "Start" },
  { key: "research", label: "AI research" },
  { key: "findings", label: "Findings" },
  { key: "your-turn", label: "Your turn" },
  { key: "review", label: "Review & save" },
] as const;

export type StageKey = (typeof STAGES)[number]["key"];

/** `?stage=` read at parse time; anything unknown is no choice at all. */
export function parseStage(raw: string | null): StageKey | null {
  return STAGES.some((s) => s.key === raw) ? (raw as StageKey) : null;
}

export const RUN_STATUS: Record<ListingAgentResearchRunStatus, { label: string; tone: StatusTone }> = {
  Queued: { label: "Queued", tone: "progress" },
  Running: { label: "Researching", tone: "progress" },
  Completed: { label: "Finished", tone: "ok" },
  Partial: { label: "Finished — partly", tone: "warn" },
  CapReached: { label: "Stopped at a limit", tone: "warn" },
  Failed: { label: "Failed", tone: "danger" },
};

/** Why a run ended, in plain words. An unknown reason shows as given. */
export const TERMINAL_REASON: Record<string, string> = {
  StopRuleMet: "Stopped once the agent's details were confirmed.",
  AllStepsDone: "Every step ran.",
  StepFailed: "A step failed. Everything found before it is kept.",
  SearchCap: "Reached the search limit for one run.",
  FetchCap: "Reached the page-read limit for one run.",
  ModelCallCap: "Reached the AI call limit for one run.",
  TimeLimit: "Reached the time limit for one run.",
  CallTimedOut: "An AI call timed out. Everything found before it is kept.",
  CostCap: "Reached the cost limit for one run.",
  DailyCeiling: "The platform's daily AI limit was reached.",
  Error: "The run hit an error. Everything found before it is kept.",
  WorkerLost: "The run stopped unexpectedly. Everything found before it is kept.",
  Disabled: "Research was switched off before this run started.",
};

export function terminalReasonText(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return TERMINAL_REASON[reason] ?? reason;
}

export const CONFIDENCE: Record<ListingAgentResearchConfidence, { label: string; tone: StatusTone }> = {
  Confirmed: { label: "Confirmed", tone: "ok" },
  Likely: { label: "Likely", tone: "info" },
  Possible: { label: "Possible", tone: "warn" },
  FirmFallback: { label: "Firm fallback", tone: "neutral" },
  ConfirmedByYou: { label: "Confirmed (by you)", tone: "ok" },
};

export const ORIGIN: Record<ListingAgentResearchOrigin, string> = {
  Clef: "From Clef",
  Web: "From the web",
  Pasted: "Pasted by you",
  OnFile: "Already on file in Phoenix",
};

/** A source labelled by its kind. Only a kind with no page to link names
 *  itself; every other source is shown by its link, as it always was. */
export const SOURCE_KIND_LABEL: Partial<Record<ListingAgentResearchSourceKind, string>> = {
  PhoenixRecord: "Already on file in Phoenix",
};

/** The run shape whose findings arrive as the Name / Email / Phone / Firm rows. */
export const FAST_RUN_SHAPE = "fast";

/** Inside a fast run's row, a finding whose own field differs from the row's
 *  is named, so the phone's type never reads as an unlabelled value. */
export const ROW_FINDING_LABEL: Record<string, string> = {
  agentFirstName: "First name",
  agentLastName: "Last name",
  firmGeneralEmail: "Firm general email",
  phoneType: "Phone type",
  firmMainPhone: "Firm main line",
};

export const STEP_LABEL: Record<string, string> = {
  start: "Start",
  clef: "Clef",
  "open-listing": "Open the listing",
  "url-repair": "Repair the listing link",
  "read-listing-page": "Read the listing page",
  trace: "Trace the agent",
  verify: "Verify",
  stop: "Stop",
  "on-file": "Check our agents",
  "listing-page": "Read the listing page",
  search: "Search for what's missing",
};

export const TRAIL_KIND_LABEL: Record<string, string> = {
  Attempted: "Tried",
  Skipped: "Skipped",
  Search: "Searched",
  Fetch: "Read a page",
  AiClassification: "AI classification",
  DeadEnd: "Dead end",
  UnsupportedDropped: "Unsupported — dropped",
  ReFetchedDeadEnd: "Re-read a known dead end",
  Note: "Note",
  Stop: "Stopped",
};

export const MATCH_FACT_LABEL: Record<string, string> = {
  title: "Title",
  city: "City",
  state: "State",
  askingPrice: "Asking price",
  cashFlow: "Cash flow",
  revenue: "Revenue",
  reference: "Reference",
};

export const MATCH_RESULT: Record<string, { label: string; tone: StatusTone }> = {
  agrees: { label: "Agrees", tone: "ok" },
  differs: { label: "Differs", tone: "danger" },
  notFound: { label: "Not on the page", tone: "neutral" },
  notOnFile: { label: "Not on file", tone: "neutral" },
};

export const BLOCKER_TEXT: Record<string, string> = {
  researchRunActive: "A run is still in progress — wait for it to finish before saving.",
  conflictingAcceptedFinding:
    "Two accepted findings disagree about one field. Reject one of them on Findings first.",
  nothingToSave: "Nothing is accepted yet. Accept the findings you want on Findings first.",
  researchAlreadySaved: "This run was already saved. Start a new run to save more.",
};

export const agentResearchCopy = {
  eyebrow: "Listing agent research",
  backToListing: "Back to listing",
  switchedOff: "Agent research is switched off, so a new run cannot be started.",
  alreadyRunning: "A run is already in progress.",
  closeThisPage: "You can close this page — we'll notify you when it finishes.",
  startScope: "Research looks for the broker's name, email and phone, and usually takes about a minute.",
  noRunYet: "No research has run on this listing yet. Start one on Start.",
  noFindings: "This run found nothing to review.",
  aiJudgement: "The AI's judgement",
  runAgainNote:
    "A new run starts from what you add here. Pages that were dead ends last time are left out.",
  nothingToSave: "Nothing to save yet. Accept the findings you want on Findings first.",
  conflictingAccepted: "Another finding for this field is already accepted. Reject it first.",
  alreadySaved: "This finding has already been saved.",
  savedToast: "Saved to the listing.",
  waitForRun: "A run is still in progress — wait for it to finish before saving.",
};
